/**
 * WalletPanel — Overview-tab hero card for the parent's Otter Coin wallet.
 *
 * Wraps CoinBalancePanels (CCM-02: two distinct coin tiles, never merged) inside a
 * titled card with a "Manage Plan & Coins" action that routes to the Plan & Coins tab
 * (subscription state + top-up packs + balances). The welcome-credit banner is
 * intentionally NOT rendered here — the Overview tab renders it once above the hero row
 * to avoid duplication (KB TO-LL-007).
 *
 * Redesign (2026-06): new component for the parent dashboard redesign. Reuses
 * CoinBalancePanels verbatim so all coin contract/QA tests remain valid.
 *
 * Modified TS-017 (T7): added optional `onTopupDetail` prop, forwarded verbatim to
 * CoinBalancePanels so the "Top-up Coins" tile opens the parent-only detail screen.
 * Optional/undefined by default — every existing WalletPanel render site without the
 * prop keeps rendering the tile as a plain non-interactive <div>.
 */

import { CoinBalancePanels } from '@/components/CoinBalancePanels';
import { Button } from '@/components/ui/button';
import { SlidersHorizontal } from 'lucide-react';
import type { OtterCoinBalance } from '@/types/coins';

export interface WalletPanelProps {
  /** The parent's two-bucket Otter Coin balance (CCM-02). */
  balance: OtterCoinBalance;
  /**
   * Called when "Manage Plan & Coins" is clicked — the page switches to the Plan & Coins tab.
   * (The button does not purchase directly; it opens the tab where the parent subscribes,
   * manages their subscription, or buys top-up packs.)
   */
  onManage?: () => void;
  /**
   * TS-017: called when the parent activates the "Top-up Coins" tile — forwarded
   * verbatim to CoinBalancePanels.onTopupDetail. Optional; when omitted, the tile
   * stays non-interactive (unchanged from pre-TS-017 behavior).
   */
  onTopupDetail?: () => void;
}

export function WalletPanel({ balance, onManage, onTopupDetail }: WalletPanelProps) {
  return (
    <div className="flex h-full flex-col rounded-3xl border border-otter-sand bg-white p-5 shadow-md">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-pebbles-400">
        <span aria-hidden="true">🪙</span> Your Coins
      </p>

      {/* CCM-02: two distinct coin tiles — never merged. Welcome banner is owned by the page. */}
      <div className="mb-4">
        <CoinBalancePanels
          balance={balance}
          isNewFamily={false}
          onTopupDetail={onTopupDetail}
        />
      </div>

      {onManage && (
        <Button
          variant="warm"
          onClick={onManage}
          className="mt-auto w-full gap-1.5 font-bold shadow-md"
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" /> Manage Plan &amp; Coins
        </Button>
      )}
    </div>
  );
}
